import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, Globe, Menu, X, ChevronDown } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { Button } from "../ui/button";

const LanguageSwitcher = ({ compact }) => {
  const { lang, setLang, languages } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const active = languages.find((l) => l.code === lang);

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const select = (code) => {
    setLang(code);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium text-neutral-700 hover:bg-neutral-100 transition-colors"
      >
        <Globe className="w-4 h-4" />
        {!compact && <span>{active?.flag} {active?.label}</span>}
        {compact && <span>{active?.flag}</span>}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute end-0 mt-2 w-44 bg-white rounded-2xl shadow-xl border border-neutral-100 p-1.5 z-50 animate-scale-in origin-top">
          {languages.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => select(l.code)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                l.code === lang ? "bg-neutral-900 text-white" : "hover:bg-neutral-100 text-neutral-700"
              }`}
            >
              <span className="text-base">{l.flag}</span>
              <span className="font-medium">{l.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Header = ({ onSearch }) => {
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { key: "experts", href: "#experts" },
    { key: "categories", href: "#categories" },
    { key: "how", href: "#how" },
    { key: "about", href: "#about" },
  ];

  const go = (href) => {
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + href); // e.g. "/#experts" — Home scrolls on mount
    } else {
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled ? "glass border-b border-neutral-200/70 py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <div className="w-9 h-9 rounded-xl bg-neutral-900 flex items-center justify-center">
            <span className="text-white font-display font-extrabold text-lg">i</span>
          </div>
          <span className="font-display font-extrabold text-xl tracking-tight text-neutral-900">intro</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <button
              key={l.key}
              onClick={() => go(l.href)}
              className="px-4 py-2 rounded-full text-sm font-medium text-neutral-700 hover:bg-neutral-100 transition-colors"
            >
              {t(`nav.${l.key}`)}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={() => go("#experts")}
            className="hidden sm:flex w-9 h-9 items-center justify-center rounded-full hover:bg-neutral-100 transition-colors text-neutral-700"
          >
            <Search className="w-4.5 h-4.5" />
          </button>
          <div className="hidden sm:block"><LanguageSwitcher compact /></div>
          <Button
            onClick={() => navigate("/admin/login")}
            variant="ghost"
            className="hidden md:inline-flex rounded-full font-medium text-neutral-700"
          >
            {t("nav.login")}
          </Button>
          <Button
            onClick={() => navigate("/become-expert")}
            className="rounded-full bg-neutral-900 hover:bg-neutral-800 text-white font-semibold px-5"
          >
            {t("nav.becomeExpert")}
          </Button>
          <button
            onClick={() => setMobileOpen((o) => !o)}
            className="lg:hidden w-9 h-9 flex items-center justify-center rounded-full hover:bg-neutral-100"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden mt-3 mx-4 bg-white rounded-3xl shadow-xl border border-neutral-100 p-3 animate-scale-in origin-top">
          {links.map((l) => (
            <button
              key={l.key}
              onClick={() => go(l.href)}
              className="w-full text-start px-4 py-3 rounded-2xl text-neutral-800 font-medium hover:bg-neutral-100"
            >
              {t(`nav.${l.key}`)}
            </button>
          ))}
          <div className="px-2 py-2"><LanguageSwitcher /></div>
        </div>
      )}
    </header>
  );
};

export default Header;
export { LanguageSwitcher };
